import { FunctionComponent, useState } from "react";
import Lightbox, { SlideImage } from "yet-another-react-lightbox";
import { Captions } from "yet-another-react-lightbox/plugins";
import {
  getPictureWithSizedURL,
  getPicturesById,
} from "../../selectors/pictures.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import {
  BaseModuleProps,
  ImageGalleryLayout,
  ImageGalleryModuleSettings,
  ImageModuleSettings,
  Picture,
  Pictures,
  TranslatedModule,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getModulesByParentId,
  getTranslatedModule,
} from "../../utils/utils.js";
import ImageGalleryLayout1 from "../ImageGalleryLayout1.js";
import ImageGalleryLayout2 from "../ImageGalleryLayout2.js";
import ImageGalleryLayout3 from "../ImageGalleryLayout3.js";
import ImageGalleryLayout4 from "../ImageGalleryLayout4.js";
import ImageGalleryLayout5 from "../ImageGalleryLayout5.js";
import ImageGalleryLayout6 from "../ImageGalleryLayout6.js";
import ImageGalleryLayout7 from "../ImageGalleryLayout7.js";
import ImageGalleryLayout8 from "../ImageGalleryLayout8.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

interface Props extends BaseModuleProps<ImageGalleryModuleSettings> {}

const clampImageSizes = (
  picture: Picture,
  maxWidth: number,
  maxHeight: number,
) => {
  const scale = Math.min(
    1,
    maxWidth / picture.width,
    maxHeight / picture.height,
  );
  const width = Math.round(picture.width * scale);
  const height = Math.round(picture.height * scale);
  return { width, height };
};

const ImageGalleryModule: FunctionComponent<Props> = ({
  translatedModule,
  translatedModule: {
    settings: { textAlign, layout },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  isFirstOnPage,
  isPreview,
  pageId,
  queries,
  isActive,
  activeModuleId,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const mediaLibraryLoaded = useAppSelector(
    (state) => state.loadStates.mediaLibrary.pictures === "loaded",
  );
  const modules = useAppSelector((state) => state.modules);
  const pictures = useAppSelector((state) => state.mediaLibrary.pictures);

  const allImagesByParentId = getModulesByParentId<ImageModuleSettings>(
    modules,
    translatedModule.id,
    pageId,
    "ImageModule",
  ).map((module) => getTranslatedModule(module, languageId));
  const imagesByParentId = filterImagesByLayout(allImagesByParentId, layout);

  const pictureIds = imagesByParentId.map(
    (imageModule) => imageModule.settings.pictureId,
  );

  const slides = getSlides(pictures, pictureIds);

  const [openIndex, setOpenIndex] = useState(-1);

  if (!mediaLibraryLoaded) return null;

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={translatedModule.id}
      colors={{ background: scheme.main.background }}
    >
      {layout === "layout-1" && (
        <ImageGalleryLayout1
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-2" && (
        <ImageGalleryLayout2
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          activeModuleId={activeModuleId}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-3" && (
        <ImageGalleryLayout3
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          activeModuleId={activeModuleId}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-4" && (
        <ImageGalleryLayout4
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-5" && (
        <ImageGalleryLayout5
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          activeModuleId={activeModuleId}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-6" && (
        <ImageGalleryLayout6
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-7" && (
        <ImageGalleryLayout7
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      {layout === "layout-8" && (
        <ImageGalleryLayout8
          title={title}
          subtitle={subtitle}
          imageModules={imagesByParentId}
          queries={queries}
          slides={slides}
          scheme={scheme}
          textAlign={textAlign}
          isFirstOnPage={isFirstOnPage}
          isPreview={isPreview}
          languageId={languageId}
          pageId={pageId}
          moduleId={translatedModule.id}
          activeModuleId={activeModuleId}
          onClickImage={(index) => {
            if (isPreview && !isActive) return;
            setOpenIndex(index);
          }}
        />
      )}

      <Lightbox
        open={openIndex >= 0}
        index={openIndex}
        close={() => setOpenIndex(-1)}
        slides={slides}
        plugins={[Captions]}
        animation={{ navigation: 0 }}
      />
    </ModuleWithHeadings>
  );
};

const filterImagesByLayout = (
  imagesByParentId: TranslatedModule<ImageModuleSettings>[],
  layout: ImageGalleryLayout,
) => {
  if (layout === "layout-4") {
    return imagesByParentId.slice(0, 4);
  }

  if (layout === "layout-6") {
    return imagesByParentId.slice(0, 14);
  }

  if (layout === "layout-7") {
    return imagesByParentId.slice(0, 4);
  }

  if (layout === "layout-8") {
    return imagesByParentId.slice(0, 6);
  }

  return imagesByParentId;
};

const getSlides = (pictures: Pictures, pictureIds: string[]): SlideImage[] => {
  if (!pictureIds.length) return [];

  return getPicturesById(pictures, pictureIds).map((picture) => {
    const { height, width } = clampImageSizes(picture, 1920, 1080);
    const { url, title } = getPictureWithSizedURL({ height, width, mode: 4 })(
      picture,
    );

    return { src: url, width, height, alt: title ?? "", description: title };
  });
};

export default ImageGalleryModule;
